import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { RegionSetStatusType } from "@app/projects/enumerators";
import {
  PciDashboardCounty,
  PciDashboardScenarioTypeEnum,
  PciDashboardSummaryType,
} from "@app/projects/network-assessment/pci-dashboard/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { cloneDeep } from "lodash";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ClaimsActivityGhostProviderScenarioModel } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityGhostProviderScenarioModel";
import { ClaimsActivityProviderScenario } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityProviderScenario";
import { ClaimsActivityProviderSummary } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityProviderSummary";
import { ClaimsActivityTopGhostProviderModel } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityTopGhostProviderModel";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity/ClaimsActivityVolumeLabelEnum";
import { PciDashboardFiltersContext } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersContext";
import { PciDashboardFiltersPayload } from "@app/projects/network-assessment/pci-dashboard/shared/PciDashboardFiltersPayload";
import PciDashboardService from "@app/projects/network-assessment/pci-dashboard/shared/pciDashboardService";

export interface ClaimsActivityDashboardContextValues {
  getGhostScenarioWidgetData: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  getTopGhostWidgetData: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  ghostProviderScenarioCountiesLoading: boolean;
  ghostProviderScenarioCountiesModel: ClaimsActivityGhostProviderScenarioModel;
  ghostProviderScenarioSpecialtiesLoading: boolean;
  ghostProviderScenarioSpecialtiesModel: ClaimsActivityGhostProviderScenarioModel;
  marketProvidersSummaries: ClaimsActivityProviderSummary[];
  marketProvidersSummariesLoading: boolean;
  networkProviderScenarios: ClaimsActivityProviderScenario[];
  networkProviderScenariosLoading: boolean;
  networkProvidersSummaries: ClaimsActivityProviderSummary[];
  networkProvidersSummariesLoading: boolean;
  onApplyFiltersClick: (filtersPayload: PciDashboardFiltersPayload) => Promise<void>;
  onDashboardDetailsViewClick: (
    claimsVolumeLabel: ClaimsActivityVolumeLabelEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => void;
  onTopGhostDashboardDetailsViewClick: (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum,
) => void;
  prospectProvidersSummaries: ClaimsActivityProviderSummary[];
  prospectProvidersSummariesLoading: boolean;
  setShowDashboardDetails: (showDashboardDetails: boolean) => void;
  showDashboardDetails: boolean;
  topGhostProviderCountiesLoading: boolean;
  topGhostProviderCountiesModel: ClaimsActivityTopGhostProviderModel;
  topGhostProviderSpecialtiesModel: ClaimsActivityTopGhostProviderModel;
  topGhostProviderSpecialtiesLoading: boolean;
}

const initialValues: ClaimsActivityDashboardContextValues = {
  getGhostScenarioWidgetData: () => null,
  getTopGhostWidgetData: () => null,
  ghostProviderScenarioCountiesLoading: true,
  ghostProviderScenarioCountiesModel: null,
  ghostProviderScenarioSpecialtiesLoading: true,
  ghostProviderScenarioSpecialtiesModel: null,
  marketProvidersSummaries: [],
  marketProvidersSummariesLoading: true,
  networkProviderScenarios: [],
  networkProviderScenariosLoading: true,
  networkProvidersSummaries: [],
  networkProvidersSummariesLoading: true,
  onApplyFiltersClick: () => null,
  onDashboardDetailsViewClick: () => null,
  onTopGhostDashboardDetailsViewClick: () => null,
  prospectProvidersSummaries: [],
  prospectProvidersSummariesLoading: true,
  setShowDashboardDetails: () => null, 
  showDashboardDetails: false,
  topGhostProviderCountiesLoading: true,
  topGhostProviderCountiesModel: null,
  topGhostProviderSpecialtiesLoading: true,
  topGhostProviderSpecialtiesModel: null,
}

export const ClaimsActivityDashboardContext = createContext(initialValues);

const ClaimsActivityDashboardContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [ghostProviderScenarioCountiesLoading, setGhostProviderScenarioCountiesLoading] = useState<boolean>(initialValues.ghostProviderScenarioCountiesLoading);
  const [ghostProviderScenarioCountiesModel, setGhostProviderScenarioCountiesModel] = useState<ClaimsActivityGhostProviderScenarioModel>(initialValues.ghostProviderScenarioCountiesModel);
  const [ghostProviderScenarioSpecialtiesLoading, setGhostProviderScenarioSpecialtiesLoading] = useState<boolean>(initialValues.ghostProviderScenarioSpecialtiesLoading);
  const [ghostProviderScenarioSpecialtiesModel, setGhostProviderScenarioSpecialtiesModel] = useState<ClaimsActivityGhostProviderScenarioModel>(initialValues.ghostProviderScenarioSpecialtiesModel);
  const [marketProvidersSummaries, setMarketProvidersSummaries] = useState<ClaimsActivityProviderSummary[]>(initialValues.marketProvidersSummaries);
  const [marketProvidersSummariesLoading, setMarketProvidersSummariesLoading] = useState<boolean>(initialValues.marketProvidersSummariesLoading);
  const [networkProviderScenarios, setNetworkProviderScenarios] = useState<ClaimsActivityProviderScenario[]>(initialValues.networkProviderScenarios);
  const [networkProviderScenariosLoading, setNetworkProviderScenariosLoading] = useState<boolean>(initialValues.networkProviderScenariosLoading);
  const [networkProvidersSummaries, setNetworkProvidersSummaries] = useState<ClaimsActivityProviderSummary[]>(initialValues.networkProvidersSummaries);
  const [networkProvidersSummariesLoading, setNetworkProvidersSummariesLoading] = useState<boolean>(initialValues.networkProvidersSummariesLoading);
  const [prospectProvidersSummaries, setProspectProvidersSummaries] = useState<ClaimsActivityProviderSummary[]>(initialValues.prospectProvidersSummaries);
  const [prospectProvidersSummariesLoading, setProspectProvidersSummariesLoading] = useState<boolean>(initialValues.prospectProvidersSummariesLoading);
  const [showDashboardDetails, setShowDashboardDetails] = useState<boolean>(initialValues.showDashboardDetails);
  const [topGhostProviderCountiesLoading, setTopGhostProviderCountiesLoading] = useState<boolean>(initialValues.topGhostProviderCountiesLoading);
  const [topGhostProviderCountiesModel, setTopGhostProviderCountiesModel] = useState<ClaimsActivityTopGhostProviderModel>(initialValues.topGhostProviderCountiesModel);
  const [topGhostProviderSpecialtiesLoading, setTopGhostProviderSpecialtiesLoading] = useState<boolean>(initialValues.topGhostProviderSpecialtiesLoading);
  const [topGhostProviderSpecialtiesModel, setTopGhostProviderSpecialtiesModel] = useState<ClaimsActivityTopGhostProviderModel>(initialValues.topGhostProviderSpecialtiesModel);

  const pciDashboardService = new PciDashboardService();
  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);
  const projectContext = useContext(CountyProjectContext);
  const [error, setError] = useState("");

  const onApplyFiltersClick = async (filtersPayload: PciDashboardFiltersPayload): Promise<void> => {
    let isFiltering = false;
    if (!topGhostProviderCountiesLoading && !topGhostProviderSpecialtiesLoading) {
      isFiltering = true;
      }

    const payload: PciDashboardFiltersPayload = cloneDeep(filtersPayload);

    // if accessing the cached model due to all of the counties, states, and specialties being selected,
    // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
    if (payload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
      payload.countySSAs = [];
    }

    if (payload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
      payload.specialtyGroupCodes = [];
    }

    if (!payload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
      payload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
      payload.countyStatuses = [];
    }

    if (!filtersPayload.compareTimeslices) {
      setMarketProvidersSummariesLoading(true);
      setNetworkProvidersSummariesLoading(true);
      setProspectProvidersSummariesLoading(true);
      setTopGhostProviderCountiesLoading(true);
      setTopGhostProviderSpecialtiesLoading(true);
      getProviderWidgetData(payload);
      getTopGhostWidgetData(payload, PciDashboardSummaryType.County, null, null, null, isFiltering);
      getTopGhostWidgetData(payload, PciDashboardSummaryType.Specialty, null, null, null, isFiltering);
    } else {
      setNetworkProviderScenariosLoading(true);
      setGhostProviderScenarioCountiesLoading(true);
      setGhostProviderScenarioSpecialtiesLoading(true);
      getProviderScenarioWidgetData(payload);
      getGhostScenarioWidgetData(payload, PciDashboardSummaryType.County, null, null, null, isFiltering);
      getGhostScenarioWidgetData(payload, PciDashboardSummaryType.Specialty, null, null, null, isFiltering);
    }
  }

  const formatScenarioSummaries = (summaries: ClaimsActivityProviderScenario[]): ClaimsActivityProviderScenario[] => {
    // provider widget summaries should always have all possible claims volume labels present in their data
    // if data does not exist, create it
    // create a new array of summaries in order to always display the claims volume labels in the same order
    const newSummaries: ClaimsActivityProviderScenario[] = [];

    for (const claimsVolumeLabel of Object.values(ClaimsActivityVolumeLabelEnum)) {
      let summary: ClaimsActivityProviderScenario = summaries.find(x => x.claimsVolumeLabel === claimsVolumeLabel);

      if (!summary) {
        summary = {
          changeCount: null,
          changePercentage: null,
          claimsVolumeLabel,
          comparePercentage: null,
          compareServicingCount: null,
          percentage: null,
          servicingCount: null,
        };
      }

      newSummaries.push(summary);
    }

    return newSummaries;
  }

  const formatSummaries = (summaries: ClaimsActivityProviderSummary[]): ClaimsActivityProviderSummary[] => {
    // provider widget summaries should always have all possible claims volume labels present in their data
    // if data does not exist, create it
    // create a new array of summaries in order to always display the claims volume labels in the same order
    const newSummaries: ClaimsActivityProviderSummary[] = [];

    for (const claimsVolumeLabel of Object.values(ClaimsActivityVolumeLabelEnum)) {
      let summary: ClaimsActivityProviderSummary = summaries.find(x => x.claimsVolumeLabel === claimsVolumeLabel);

      if (!summary) {
        summary = {
          claimsVolumeLabel,
          percentage: null,
          servicingCount: null
        };
      }

      newSummaries.push(summary);
    }

    return newSummaries;
  }

  const getGhostScenarioWidgetData = (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string = null,
    sortDirection: SortDirection = null,
    isSorting?: boolean,
    isFiltering?: boolean,
  ): void => {
    if (!filtersPayload) {
      filtersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

      // if accessing the cached model due to all of the counties, states, and specialties being selected,
      // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
      if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
        filtersPayload.countySSAs = [];
      }

      if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
        filtersPayload.specialtyGroupCodes = [];
      }

      if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) && 
        filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
        filtersPayload.countyStatuses = [];
      }
    }

    if (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
    }

    filtersPayload.sortedColumn = sortedColumn;
    filtersPayload.sortDirection = sortDirection;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        filtersPayload.take = isSorting ? ghostProviderScenarioCountiesModel.records.length : ((!ghostProviderScenarioCountiesModel || isFiltering) ? 10 : ghostProviderScenarioCountiesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !ghostProviderScenarioCountiesModel) ? 0 : ghostProviderScenarioCountiesModel.records.length;

        pciDashboardService.getClaimsActivityGhostScenarioCountyWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !ghostProviderScenarioCountiesModel) {
            setGhostProviderScenarioCountiesModel(data);
          } else {
            const newModel: ClaimsActivityGhostProviderScenarioModel = cloneDeep(ghostProviderScenarioCountiesModel);
            newModel.records = newModel.records.concat(data.records);
            setGhostProviderScenarioCountiesModel(newModel);
          }

          setGhostProviderScenarioCountiesLoading(false);
        }, (error) => {
          setGhostProviderScenarioCountiesLoading(false);
          setError(error);
        })
        break;
      case PciDashboardSummaryType.Specialty:
        filtersPayload.take = isSorting ? ghostProviderScenarioSpecialtiesModel.records.length : ((!ghostProviderScenarioSpecialtiesModel || isFiltering) ? 10 : ghostProviderScenarioSpecialtiesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !ghostProviderScenarioSpecialtiesModel) ? 0 : ghostProviderScenarioSpecialtiesModel.records.length;

        pciDashboardService.getClaimsActivityGhostScenarioSpecialtyWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !ghostProviderScenarioSpecialtiesModel) {
            setGhostProviderScenarioSpecialtiesModel(data);
          } else {
            const newModel: ClaimsActivityGhostProviderScenarioModel = cloneDeep(ghostProviderScenarioSpecialtiesModel);
            newModel.records = newModel.records.concat(data.records);
            setGhostProviderScenarioSpecialtiesModel(newModel);
          }

          setGhostProviderScenarioSpecialtiesLoading(false);
        }, (error) => {
          setGhostProviderScenarioSpecialtiesLoading(false);
          setError(error);
        })
        break;
      default:
        break;
    }
  }

  const getTopGhostWidgetData = (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string = null,
    sortDirection: SortDirection = null,
    isSorting?: boolean,
    isFiltering?: boolean,
  ): void => {
    if (!filtersPayload) {
      filtersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);

      // if accessing the cached model due to all of the counties, states, and specialties being selected,
      // display all those filters as selected in the filters applied accordion, but pass empty arrays to the api
      if (filtersPayload.countySSAs.length === pciDashboardFiltersContext.counties.length) {
        filtersPayload.countySSAs = [];
      }

      if (filtersPayload.specialtyGroupCodes.length === pciDashboardFiltersContext.specialties.length) {
        filtersPayload.specialtyGroupCodes = [];
      }

      if (!filtersPayload.countyStatuses.includes(RegionSetStatusType.Opportunity) &&
        filtersPayload.countyStatuses.length === pciDashboardFiltersContext.countyStatuses.filter(x => x.key !== RegionSetStatusType.Opportunity).length) {
        filtersPayload.countyStatuses = [];
      }
    }

    if (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
    }

    filtersPayload.sortedColumn = sortedColumn;
    filtersPayload.sortDirection = sortDirection;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        filtersPayload.take = isSorting ? topGhostProviderCountiesModel.records.length : ((!topGhostProviderCountiesModel || isFiltering) ? 10 : topGhostProviderCountiesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !topGhostProviderCountiesModel) ? 0 : topGhostProviderCountiesModel.records.length;

        pciDashboardService.getClaimsActivityTopGhostCountyWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !topGhostProviderCountiesModel) {
            setTopGhostProviderCountiesModel(data);
          } else {
            const newModel: ClaimsActivityTopGhostProviderModel = cloneDeep(topGhostProviderCountiesModel);
            newModel.records = newModel.records.concat(data.records);
            setTopGhostProviderCountiesModel(newModel);
          }

          setTopGhostProviderCountiesLoading(false);
        }, (error) => {
          setTopGhostProviderCountiesLoading(false);
          setError(error);
        });

        break;
      case PciDashboardSummaryType.Specialty:
        filtersPayload.take = isSorting ? topGhostProviderSpecialtiesModel.records.length : ((!topGhostProviderSpecialtiesModel || isFiltering) ? 10 : topGhostProviderSpecialtiesModel.totalCount);
        filtersPayload.skip = (isSorting || isFiltering || !topGhostProviderSpecialtiesModel) ? 0 : topGhostProviderSpecialtiesModel.records.length;

        pciDashboardService.getClaimsActivityTopGhostSpecialtyWidgetData(
          projectContext.project.projectId,
          pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
          filtersPayload
        ).then((data) => {
          if (isSorting || isFiltering || !topGhostProviderSpecialtiesModel) {
            setTopGhostProviderSpecialtiesModel(data);
          } else {
            const newModel: ClaimsActivityTopGhostProviderModel = cloneDeep(topGhostProviderSpecialtiesModel);
            newModel.records = newModel.records.concat(data.records);
            setTopGhostProviderSpecialtiesModel(newModel);
          }

          setTopGhostProviderSpecialtiesLoading(false);
        }, (error) => {
          setTopGhostProviderSpecialtiesLoading(false);
          setError(error);
        })
        break;
      default:
        break;
    }
  }

  const getProviderScenarioWidgetData = (filtersPayload: PciDashboardFiltersPayload): void => {
    filtersPayload.providerNetworkType = ProviderNetworkTypeEnum.Network;

    pciDashboardService.getClaimsActivityProviderScenarioWidgetData(
      projectContext.project.projectId,
      pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
      filtersPayload
    ).then((data) => {
      data = formatScenarioSummaries(data);
      setNetworkProviderScenarios(data);
      setNetworkProviderScenariosLoading(false);
    }, (error) => {
      setNetworkProviderScenariosLoading(false);
      setError(error);
    });
  }

  const getProviderWidgetData = (filtersPayload: PciDashboardFiltersPayload): void => {
    const providerNetworkTypes: ProviderNetworkTypeEnum[] = [ProviderNetworkTypeEnum.Market, ProviderNetworkTypeEnum.Network, ProviderNetworkTypeEnum.Prospect];

    for (const providerNetworkType of providerNetworkTypes) {
      filtersPayload.providerNetworkType = providerNetworkType;

      pciDashboardService.getClaimsActivityProviderWidgetData(
        projectContext.project.projectId,
        pciDashboardFiltersContext.projectRunConfiguration.projectRunConfigurationId,
        filtersPayload
      ).then((data) => {
        data = formatSummaries(data);

        switch (providerNetworkType) {
          case ProviderNetworkTypeEnum.Market:
            setMarketProvidersSummaries(data);
            setMarketProvidersSummariesLoading(false);
            break;
          case ProviderNetworkTypeEnum.Network:
            setNetworkProvidersSummaries(data);
            setNetworkProvidersSummariesLoading(false);
            break;
          case ProviderNetworkTypeEnum.Prospect:
            setProspectProvidersSummaries(data);
            setProspectProvidersSummariesLoading(false);
            break;
          default:
            break;
        }
      }, (error) => {
        setError(error);
      });
    }
  }

  const onDashboardDetailsViewClick = (
    claimsLabel: ClaimsActivityVolumeLabelEnum,
    providerNetworkType: ProviderNetworkTypeEnum,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => {
    const filters: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filters.claimsLabel = claimsLabel;
    filters.providerNetworkType = providerNetworkType;
    filters.scenario = scenario;
    pciDashboardFiltersContext.setDisplayedFilters(filters);
    setShowDashboardDetails(true);
  }

  const onTopGhostDashboardDetailsViewClick = (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => {
    const filters: PciDashboardFiltersPayload = cloneDeep(pciDashboardFiltersContext.displayedFilters);
    filters.claimsLabel = ClaimsActivityVolumeLabelEnum.Ghost;
    filters.providerNetworkType = ProviderNetworkTypeEnum.Network;
    filters.scenario = scenario;

    switch (summaryType) {
      case PciDashboardSummaryType.County:
        const county: PciDashboardCounty = pciDashboardFiltersContext.counties.find(x => x.countyName === dashboardDetailsParam);

        if (!!county) {
          filters.countySSAs = [county.countySSA];
        }

        break;
      case PciDashboardSummaryType.Specialty:
        filters.specialtyGroupCodes = [dashboardDetailsParam];
        break;
      default:
        break;
    }

    pciDashboardFiltersContext.setDisplayedFilters(filters);
    setShowDashboardDetails(true);
  }

  useEffect(() => {
    if (!!error) {
      throw error;
    }
  }, [error]);

  return (
    <ClaimsActivityDashboardContext.Provider
      value={{
        getGhostScenarioWidgetData,
        getTopGhostWidgetData,
        ghostProviderScenarioCountiesLoading,
        ghostProviderScenarioCountiesModel,
        ghostProviderScenarioSpecialtiesLoading,
        ghostProviderScenarioSpecialtiesModel,
        marketProvidersSummaries,
        marketProvidersSummariesLoading,
        networkProviderScenarios,
        networkProviderScenariosLoading,
        networkProvidersSummaries,
        networkProvidersSummariesLoading,
        onApplyFiltersClick,
        onDashboardDetailsViewClick,
        onTopGhostDashboardDetailsViewClick,
        prospectProvidersSummaries,
        prospectProvidersSummariesLoading,
        setShowDashboardDetails,
        showDashboardDetails,
        topGhostProviderCountiesLoading,
        topGhostProviderCountiesModel,
        topGhostProviderSpecialtiesLoading,
        topGhostProviderSpecialtiesModel,
      }}>
      {props.children}
    </ClaimsActivityDashboardContext.Provider>
  )
}

export default ClaimsActivityDashboardContextProvider;
